import { absoluteTime, dayAndTime, relativeTime } from "../lib/time";
import type { TicketActivity } from "../types";

/**
 * One state change, in the thread beside the messages that caused it.
 *
 * The HubSpot pattern: who assigned this, who reopened it, when it changed
 * pipeline -- read in sequence with the conversation rather than filed in a
 * separate audit tab. The two only make sense together. "Closed" three minutes
 * after a reply is a resolution; the same line three minutes after a customer's
 * angry follow-up is something else entirely, and a separate tab loses that.
 *
 * ## Deliberately not a bubble
 *
 * These are one grey line with a rule through them, and that is load-bearing
 * rather than decorative. A thread's three bubble styles already mean something
 * precise -- inbound, outbound, and internal note, the last of which must never
 * be mistaken for something the customer saw. A fourth bubble style would
 * dilute that distinction to save a change of shape. Metadata should not look
 * like correspondence.
 */

/**
 * Events that earn a line in the thread.
 *
 * `message_added`, `note_added` and `created` are all deliberately absent: the
 * message they describe is already in the thread, two inches away, and showing
 * both would double the length of every conversation to say nothing new.
 *
 * Filtered here rather than server-side because it is a presentation call. The
 * endpoint returns the whole audit trail, which is what an audit trail is for.
 */
const SHOWN: ReadonlySet<string> = new Set([
  "status_changed",
  "assigned",
  "unassigned",
  "pipeline_changed",
  "channel_changed",
  "references_changed",
  "automation_applied",
  "merged",
  "spam_marked",
  "spam_cleared",
  "reopened",
  "subject_changed",
]);

export function isThreadActivity(entry: TicketActivity): boolean {
  return SHOWN.has(entry.type);
}

/** A name for whoever caused this, or null when nobody did. */
function actorOf(entry: TicketActivity): string | null {
  return entry.actorName;
}

/**
 * What happened, as a sentence fragment following the actor's name.
 *
 * Returns the part after "Jade ", so the caller can render the name however it
 * likes -- or drop it entirely for the events nobody triggered.
 *
 * Falls back to a readable form of the raw type rather than to nothing. A new
 * event added to the catalogue and not to this file should read a little
 * stiffly, not vanish from the history: an audit trail that silently omits
 * what it does not recognise is worse than one that is occasionally terse.
 */
function phraseFor(entry: TicketActivity): string {
  const to = entry.toLabel;
  const from = entry.fromLabel;

  switch (entry.type) {
    case "assigned":
      /* "reassigned … from …" only when there was somebody to take it off.
         Most assignments are the first, and "reassigned from nobody" is worse
         than silence about it. */
      if (to && from) return `reassigned this to ${to}, from ${from}`;
      if (to) return `assigned this to ${to}`;
      return "assigned this";

    case "unassigned":
      return from ? `unassigned this from ${from}` : "unassigned this";

    case "status_changed": {
      const next = String(entry.data?.to ?? "");
      if (next === "closed") return "closed this";
      if (next === "open") return "opened this";
      return `changed the status to ${next || "something else"}`;
    }

    case "reopened":
      /* The one event routinely caused by nobody: a customer replying, or a
         Lane task finishing, both reopen without an actor. The reason rides in
         the payload for the latter. */
      return entry.data?.reason === "lane_task_completed"
        ? "reopened this — the linked Lane task was completed"
        : "reopened this";

    case "pipeline_changed":
      if (to && from) return `moved this to ${to}, from ${from}`;
      if (to) return `moved this to ${to}`;
      return "changed the pipeline";

    case "channel_changed":
      return to ? `moved this to the ${to} inbox` : "moved this to another inbox";

    case "references_changed":
      return "updated the references on this";

    case "automation_applied": {
      const rules = entry.data?.rules;
      const names = Array.isArray(rules) ? rules.filter(Boolean) : [];
      if (names.length === 1) return `triaged by the “${String(names[0])}” rule`;
      if (names.length > 1) return `triaged by ${names.length} automation rules`;
      return "triaged automatically";
    }

    case "merged":
      return "merged this with another ticket";

    case "subject_changed": {
      /* The old name, not the new one: the new one is in the header at the top
         of the screen, and the question this line answers is what the ticket
         used to be called -- which is what somebody searching for it by the
         name they remember needs to see. */
      const was = String(entry.data?.from ?? "");
      return was ? `renamed this, from “${was}”` : "renamed this";
    }

    case "spam_marked":
      return "marked this as spam";

    case "spam_cleared":
      return "marked this as not spam";

    default:
      return entry.type.replace(/_/g, " ");
  }
}

export function ActivityEntry({ entry }: { entry: TicketActivity }) {
  const actor = actorOf(entry);
  const phrase = phraseFor(entry);

  return (
    <div className="flex items-center gap-3 py-0.5 text-[11px] text-muted">
      {/* Rules either side, so the line reads as a divider in the conversation
          rather than as a very small message. */}
      <span aria-hidden className="h-px flex-1 bg-line" />

      <p className="shrink-0 text-center">
        {actor ? (
          <span className="font-medium text-content">{actor}</span>
        ) : null}
        {actor ? " " : null}
        {/* Capitalised when there is no actor in front of it, so an ingest-caused
            line still reads as a sentence rather than starting mid-thought. */}
        <span className={actor ? undefined : "first-letter:uppercase"}>
          {phrase}
        </span>
        {" · "}
        {/*
          The clock time, not "2h ago".

          An audit line is read to reconstruct a sequence -- who touched this
          before the customer rang, whether the reassignment came before or
          after the reply above it -- and elapsed time cannot answer that
          without arithmetic against a "now" the reader has to supply. "at
          13:30" is also simply how the desk repeats one of these back.

          The relative form stays in the tooltip, because "how long has this
          been sitting unassigned" is a real second question and cheap to keep.
        */}
        <time
          dateTime={entry.createdAt}
          title={`${absoluteTime(entry.createdAt)} · ${relativeTime(entry.createdAt)}`}
        >
          {dayAndTime(entry.createdAt)}
        </time>
      </p>

      <span aria-hidden className="h-px flex-1 bg-line" />
    </div>
  );
}
